/**
 * @file Custom hook that handles the upload of a photo to the moderation service and holds it's own state.
 */

import React, { useState } from "react";
import loadImage from "blueimp-load-image";
import photoModerationUpload from "../photo-moderation/moderationService";
import { PhotoModResponseData, PhotoUploadPayload } from "../../types";

const usePhotoModerationUpload = () => {
  const [base64Photo, setbase64Photo] = useState<string | null>("");
  const [photoModerationStatus, setPhotoModerationStatus] = useState<
    "success" | "error" | "idle" | null
  >(null);
  const [photoModerationError, setPhotoModerationError] = useState<object | null>(null);
  const [photoModerationLoading, setPhotoModerationLoading] = useState<boolean>(false);
  const [photoModResponse, setPhotoModResponse] = useState<PhotoModResponseData | null>(null);
  const [photoDenyReasons, setPhotoDenyReasons] = useState<string[] | null>(null);

  // This prevents subsequent rejected photos from entering an unrecoverable loading state.
  const resetPhotoModerationState = () => {
    setPhotoModerationLoading(false);
    setPhotoModerationStatus("idle");
    setPhotoModResponse(null);
    setPhotoModerationError(null);
    setPhotoDenyReasons([]);
  };

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    callback: (data: PhotoUploadPayload) => void
  ) => {
    const file = event.target.files?.[0];

    if (file) {
      validatePhoto(file);

      loadImage(
        file,
        (eventOrImage: Event | HTMLImageElement | HTMLCanvasElement) => {
          if (eventOrImage instanceof HTMLCanvasElement) {
            const canvas = eventOrImage;
            const dataURL = canvas.toDataURL("image/jpeg");
            const base64Conversion = dataURL.replace(/^data:image\/(png|jpg);base64,/, "");

            setbase64Photo(base64Conversion);
            uploadPhoto(dataURL.replace("data:image/jpeg;base64,", ""), callback);
          }
        },
        {
          maxWidth: 1000, // TODO: Should this be configurable?
          maxHeight: 1000, // TODO: Should this be configurable?
          orientation: true,
          canvas: true,
          meta: true
        }
      );
    }
  };

  const validatePhoto = (base64Photo: File) => {
    if (!base64Photo) {
      setPhotoModerationError(new Error("Invalid photo"));
      return false;
    }

    return true;
  };

  const parsePhotoRejectReason = (respData: PhotoModResponseData) => {
    const { denyReasons, accepted, imageUrl } = respData?.photoModeration || {};

    if (accepted === false && denyReasons && denyReasons.length) {
      // When We Know The denyReason
      setPhotoDenyReasons(denyReasons);
    } else if (!imageUrl) {
      setPhotoDenyReasons(["unknown"]);
    }
  };

  // Take base64 encoded image and send it to the moderation service
  const uploadPhoto = async (rawBase64: string, callback: (data: PhotoUploadPayload) => void) => {
    setPhotoModerationLoading(true);
    // Call photo moderation service
    const moderationResponse = photoModerationUpload(rawBase64);

    await moderationResponse
      .then((data) => {
        // If successful
        if ((data?.imageUrl && data?.imageHandshake) || data?.photoModeration) {
          setPhotoModerationStatus("success");
          setPhotoModerationLoading(false);
          setPhotoModResponse(data);
          callback(data);
          parsePhotoRejectReason(data);
          return data;
        } else {
          const respError = {
            ...new Error("Unable to receive photo moderation response."),
            resp: data as PhotoModResponseData
          };
          throw respError;
        }
      })
      .catch((err) => {
        // TODO: log error
        console.error(err);
        parsePhotoRejectReason(err?.resp || {});
        setPhotoModerationError(err);
        setPhotoModerationStatus("error");
        setPhotoModerationLoading(false);
      });
  };

  const photoModerationProps = {
    base64Photo,
    photoModerationStatus,
    photoModerationError,
    photoModerationLoading,
    photoModResponse,
    photoDenyReasons,
    handleFileChange,
    uploadPhoto,
    resetPhotoModerationState
  };

  return photoModerationProps;
};

export default usePhotoModerationUpload;
